import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck } from "@phosphor-icons/react";

export default function LegalModal({ open, onOpenChange, initialTab = "mentions" }) {
  const [tab, setTab] = useState(initialTab);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="legal-modal"
        className="max-w-3xl w-[calc(100%-2rem)] max-h-[92vh] p-0 overflow-hidden rounded-3xl border-gray-200 shadow-floating bg-white"
      >
        <DialogTitle className="sr-only">Documents légaux</DialogTitle>
        <DialogDescription className="sr-only">
          Mentions légales, protection des données et CGV de MP Advisory
        </DialogDescription>

        {/* Header */}
        <div className="px-6 sm:px-10 pt-7 pb-4 border-b border-gray-100">
          <div className="inline-flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-purple-soft flex items-center justify-center">
              <ShieldCheck size={14} weight="duotone" className="text-purple-accent" />
            </div>
            <span className="eyebrow text-purple-accent">Documents légaux</span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden">
          <TabsList
            data-testid="legal-modal-tabs"
            className="h-auto rounded-none bg-transparent border-b border-gray-100 px-6 sm:px-10 gap-2 sm:gap-6 justify-start overflow-x-auto"
          >
            {[
              { value: "mentions", label: "Mentions légales" },
              { value: "cgv", label: "CGV" },
              { value: "data", label: "Protection des données" },
            ].map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={`tab-${t.value}`}
                className="
                  relative px-1 py-3 rounded-none font-medium text-[14px] whitespace-nowrap
                  text-slate-500 hover:text-navy-deep
                  data-[state=active]:text-navy-deep data-[state=active]:bg-transparent
                  data-[state=active]:shadow-none
                  after:absolute after:left-0 after:right-0 after:-bottom-px after:h-[2px]
                  data-[state=active]:after:bg-purple-accent
                "
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-6 sm:px-10 py-7 max-h-[65vh]">
            <TabsContent value="mentions" className="m-0 focus-visible:outline-none">
              <MentionsContent />
            </TabsContent>
            <TabsContent value="data" className="m-0 focus-visible:outline-none">
              <DataContent />
            </TabsContent>
            <TabsContent value="cgv" className="m-0 focus-visible:outline-none">
              <CgvContent />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/* ============================== Sections ============================== */

function Section({ title, children }) {
  return (
    <section className="mb-7 last:mb-0">
      <h3 className="font-serif text-lg sm:text-xl text-navy-deep font-semibold mb-2 leading-tight">
        {title}
      </h3>
      <div className="space-y-2 text-[14px] sm:text-[15px] text-slate-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Field({ label, value }) {
  return (
    <p>
      <span className="font-semibold text-navy-deep">{label} :</span> {value}
    </p>
  );
}

function MentionsContent() {
  return (
    <div data-testid="content-mentions">
      <Section title="Éditeur du site">
        <p className="font-semibold text-navy-deep">Madame Magalie Pichon</p>
        <p>MP Advisory</p>
        <p className="italic text-slate-500">
          Cabinet de recrutement indépendant spécialisé Sales &amp; IT
        </p>
      </Section>

      <Section title="Responsable de publication">
        <p>Magalie Pichon</p>
      </Section>

      <Section title="Statut juridique">
        <p>Entreprise individuelle</p>
      </Section>

      <Section title="SIRET">
        <p className="font-mono tracking-wider">902 875 160 00027</p>
      </Section>

      <Section title="Code APE / NAF">
        <p>7022Z — Conseil pour les affaires et autres conseils de gestion</p>
      </Section>

      <Section title="Adresse">
        <p>Paris 75016</p>
      </Section>

      <Section title="Contact">
        <Field
          label="Email"
          value={
            <a href="mailto:rh@mpadvisory.fr" className="text-purple-accent hover:underline">
              rh@mpadvisory.fr
            </a>
          }
        />
        <Field
          label="Téléphone"
          value={
            <a href="tel:+33683175127" className="text-purple-accent hover:underline">
              06 83 17 51 27
            </a>
          }
        />
        <Field
          label="Site internet"
          value={
            <a
              href="https://mpadvisory.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-accent hover:underline"
            >
              https://mpadvisory.fr
            </a>
          }
        />
      </Section>

      <Section title="Hébergement">
        <p>
          Le site est hébergé par la plateforme utilisée pour son déploiement technique.
        </p>
        <p className="mt-2">
          <span className="font-semibold text-navy-deep">Hébergeur :</span> Emergent
        </p>
        <p>
          <span className="font-semibold text-navy-deep">Site :</span>{" "}
          <a
            href="https://app.emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-accent hover:underline"
          >
            https://app.emergent.sh
          </a>
        </p>
        <p className="italic text-slate-500 mt-2 text-[13px]">
          Les informations complètes relatives à l'hébergeur pourront être complétées
          ultérieurement si nécessaire.
        </p>
      </Section>

      <Section title="Propriété intellectuelle">
        <p>
          L'ensemble du contenu présent sur ce site (textes, images, logo, charte
          graphique) est la propriété exclusive de MP Advisory, sauf mention contraire.
          Toute reproduction, représentation ou diffusion, totale ou partielle, sans
          autorisation écrite préalable est interdite.
        </p>
      </Section>

      <Section title="Responsabilité">
        <p>
          MP Advisory met tout en œuvre pour fournir des informations exactes et à jour
          sur le site. Toutefois, MP Advisory ne saurait être tenue responsable des
          erreurs ou omissions, ni des conséquences résultant d'une utilisation des
          informations présentées.
        </p>
      </Section>

      <Section title="Liens externes">
        <p>
          Le site peut contenir des liens vers des sites externes. MP Advisory n'exerce
          aucun contrôle sur ces sites et ne peut être tenue responsable de leur contenu
          ou de leur disponibilité.
        </p>
      </Section>
    </div>
  );
}

function DataContent() {
  return (
    <div data-testid="content-data">
      <Section title="Responsable du traitement">
        <p>
          Le responsable du traitement des données collectées via le site est
          Madame Magalie Pichon, MP Advisory.
        </p>
        <p>
          Contact :{" "}
          <a href="mailto:rh@mpadvisory.fr" className="text-purple-accent hover:underline">
            rh@mpadvisory.fr
          </a>
        </p>
      </Section>

      <Section title="Données collectées">
        <p>
          Les données collectées via le formulaire de contact, les échanges par
          email ou les candidatures peuvent inclure : nom, prénom, adresse email,
          numéro de téléphone, parcours professionnel, CV et tout autre élément
          transmis volontairement par l'utilisateur.
        </p>
      </Section>

      <Section title="Finalités du traitement">
        <p>Les données sont utilisées pour :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>répondre aux demandes des candidats et des entreprises ;</li>
          <li>identifier et évaluer des profils dans le cadre de missions de recrutement ;</li>
          <li>échanger sur des opportunités professionnelles ;</li>
          <li>assurer le suivi de la relation commerciale.</li>
        </ul>
      </Section>

      <Section title="Base légale">
        <p>
          Le traitement repose sur le consentement de l'utilisateur lors de la
          transmission de ses données, ainsi que sur l'intérêt légitime de MP Advisory
          dans le cadre de son activité de recrutement.
        </p>
      </Section>

      <Section title="Destinataires des données">
        <p>
          Les données sont destinées exclusivement à MP Advisory et, le cas échéant,
          aux entreprises clientes dans le cadre d'un processus de recrutement, avec
          l'accord préalable du candidat.
        </p>
      </Section>

      <Section title="Durée de conservation">
        <p>
          Les données sont conservées pendant la durée nécessaire aux finalités
          mentionnées ci-dessus, et au maximum pendant deux (2) ans à compter du
          dernier échange, sauf opposition de l'utilisateur.
        </p>
      </Section>

      <Section title="Droits des utilisateurs">
        <p>
          Conformément au RGPD et à la loi Informatique et Libertés, vous disposez
          d'un droit d'accès, de rectification, d'effacement, de limitation et
          d'opposition au traitement de vos données, ainsi que d'un droit à la
          portabilité.
        </p>
        <p>
          Pour exercer ces droits, contactez :{" "}
          <a href="mailto:rh@mpadvisory.fr" className="text-purple-accent hover:underline">
            rh@mpadvisory.fr
          </a>
        </p>
      </Section>

      <Section title="Sécurité">
        <p>
          MP Advisory met en œuvre les mesures techniques et organisationnelles
          appropriées pour protéger la confidentialité et l'intégrité des données
          collectées.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          Le site peut utiliser des cookies techniques nécessaires à son bon
          fonctionnement. Aucun cookie de traçage commercial n'est utilisé sans
          consentement préalable de l'utilisateur.
        </p>
      </Section>

      <Section title="Services tiers">
        <p>
          Certaines fonctionnalités du site reposent sur des services tiers
          (formulaire Google Forms, prise de rendez-vous Calendly, hébergement
          Emergent). Ces services peuvent traiter des données conformément à
          leurs propres politiques de confidentialité.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Pour toute question relative à la protection des données :{" "}
          <a href="mailto:rh@mpadvisory.fr" className="text-purple-accent hover:underline">
            rh@mpadvisory.fr
          </a>
        </p>
      </Section>
    </div>
  );
}

function CgvContent() {
  return (
    <div data-testid="content-cgv">
      <h2 className="font-serif text-xl sm:text-2xl text-navy-deep font-semibold mb-6">
        Conditions Générales de Vente – MP Advisory
      </h2>

      <Section title="I. Présentation">
        <p>
          MP Advisory est un cabinet indépendant spécialisé dans le recrutement de
          profils Sales &amp; IT. MP Advisory accompagne les entreprises clientes
          dans l'identification, l'approche, l'évaluation et la sélection de talents
          qualifiés, en tenant compte des besoins spécifiques de chaque organisation.
        </p>
        <p>
          Les prestations s'inscrivent dans une démarche sur mesure, confidentielle,
          collaborative et orientée résultats.
        </p>
      </Section>

      <Section title="II. Dispositions générales">
        <p>
          Les présentes Conditions Générales de Vente définissent les règles
          applicables aux prestations proposées par MP Advisory à ses clients
          professionnels.
        </p>
        <p>
          Toute commande ou validation d'une proposition commerciale implique
          l'adhésion aux présentes conditions, sauf conditions particulières
          convenues par écrit entre MP Advisory et le client.
        </p>
      </Section>

      <Section title="III. Nature des prestations">
        <p>
          MP Advisory propose des prestations de recrutement et d'approche directe
          dans les domaines Sales &amp; IT.
        </p>
        <p>Les prestations peuvent notamment inclure :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>l'analyse du besoin de recrutement ;</li>
          <li>la définition du profil recherché ;</li>
          <li>l'identification des entreprises cibles et viviers de candidats ;</li>
          <li>le sourcing de candidats ;</li>
          <li>l'approche directe ;</li>
          <li>la préqualification des profils ;</li>
          <li>l'évaluation du parcours, des compétences et des motivations ;</li>
          <li>la présentation de candidats sélectionnés ;</li>
          <li>le suivi du processus de recrutement ;</li>
          <li>l'accompagnement jusqu'à l'intégration du candidat.</li>
        </ul>
      </Section>

      <Section title="IV. Modalités d'intervention">
        <p>
          Chaque mission fait l'objet d'un échange préalable avec le client afin de
          préciser le contexte, le poste, les critères de sélection, la rémunération,
          le process de recrutement et les conditions de collaboration.
        </p>
        <p>
          Les modalités précises de la mission, les honoraires, les délais de paiement
          et les éventuelles garanties sont définis dans une proposition commerciale,
          un contrat ou un accord écrit spécifique.
        </p>
      </Section>

      <Section title="V. Honoraires et conditions financières">
        <p>
          Les honoraires de MP Advisory sont définis selon les modalités convenues
          avec le client avant le démarrage de la mission.
        </p>
        <p>Ils peuvent être calculés sous forme :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>d'un pourcentage de la rémunération annuelle brute du candidat recruté ;</li>
          <li>d'un forfait ;</li>
          <li>ou de toute autre modalité validée par écrit entre les parties.</li>
        </ul>
        <p className="italic text-slate-500 mt-3 text-[13px]">
          Aucun tarif précis n'est affiché dans les présentes CGV. Les conditions
          tarifaires applicables sont communiquées dans la proposition commerciale.
        </p>
      </Section>

      <Section title="VI. Facturation et paiement">
        <p>
          Les conditions de facturation, les échéances de paiement et les modalités
          de règlement sont précisées dans la proposition commerciale ou le contrat
          conclu avec le client.
        </p>
        <p>
          Sauf accord contraire, les factures sont payables selon les délais indiqués
          sur la facture.
        </p>
        <p>
          En cas de retard de paiement, des pénalités de retard peuvent être
          appliquées conformément à la réglementation en vigueur.
        </p>
      </Section>

      <Section title="VII. Obligations du client">
        <p>
          Le client s'engage à fournir à MP Advisory toutes les informations
          nécessaires à la bonne réalisation de la mission, notamment :
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>le descriptif du poste ;</li>
          <li>les critères de sélection ;</li>
          <li>la rémunération proposée ;</li>
          <li>la localisation ;</li>
          <li>le contexte de recrutement ;</li>
          <li>les étapes du processus de sélection ;</li>
          <li>les interlocuteurs impliqués dans le recrutement.</li>
        </ul>
        <p>
          Le client s'engage également à informer MP Advisory de l'avancement du
          processus, des entretiens réalisés et de toute décision concernant les
          candidats présentés.
        </p>
      </Section>

      <Section title="VIII. Obligations de MP Advisory">
        <p>
          MP Advisory s'engage à réaliser ses prestations avec sérieux,
          professionnalisme, confidentialité et diligence.
        </p>
        <p>
          MP Advisory met en œuvre les moyens nécessaires pour identifier et
          présenter des candidats correspondant au besoin exprimé par le client.
        </p>
        <p>
          MP Advisory est tenue à une obligation de moyens et non à une obligation
          de résultat.
        </p>
      </Section>

      <Section title="IX. Présentation des candidats">
        <p>
          Les candidats présentés par MP Advisory sont sélectionnés sur la base des
          informations disponibles, de leur parcours, de leurs compétences, de
          leurs motivations et de leur adéquation avec le besoin du client.
        </p>
        <p>La décision finale de recrutement appartient exclusivement au client.</p>
      </Section>

      <Section title="X. Confidentialité">
        <p>
          Les informations échangées entre MP Advisory, le client et les candidats
          sont strictement confidentielles.
        </p>
        <p>
          Le client s'engage à ne pas transmettre à des tiers les informations
          relatives aux candidats présentés sans accord préalable.
        </p>
        <p>
          MP Advisory s'engage également à traiter les informations clients et
          candidats avec confidentialité.
        </p>
      </Section>

      <Section title="XI. Données personnelles">
        <p>
          Les données personnelles collectées dans le cadre des missions de
          recrutement sont utilisées uniquement pour les finalités liées au
          recrutement, à la mise en relation professionnelle et au suivi des
          échanges.
        </p>
        <p>
          Les données sont traitées conformément à la politique de protection des
          données disponible sur le site.
        </p>
      </Section>

      <Section title="XII. Garantie éventuelle">
        <p>
          Si une garantie de remplacement ou de reprise est prévue, ses modalités
          doivent être précisées dans la proposition commerciale ou le contrat
          conclu avec le client.
        </p>
        <p>
          À défaut de mention spécifique dans l'accord commercial, aucune garantie
          automatique ne s'applique.
        </p>
      </Section>

      <Section title="XIII. Responsabilité">
        <p>
          MP Advisory ne peut être tenue responsable des décisions prises par le
          client, du comportement ultérieur d'un candidat recruté ou de la rupture
          d'une relation contractuelle entre le client et le candidat.
        </p>
        <p>
          La responsabilité de MP Advisory est limitée aux prestations effectivement
          réalisées dans le cadre de la mission confiée.
        </p>
      </Section>

      <Section title="XIV. Propriété intellectuelle">
        <p>
          Les documents, supports, méthodes, analyses, messages, contenus et
          éléments transmis par MP Advisory dans le cadre de ses prestations
          restent sa propriété, sauf accord écrit contraire.
        </p>
        <p>Toute reproduction, diffusion ou utilisation non autorisée est interdite.</p>
      </Section>

      <Section title="XV. Droit applicable et litiges">
        <p>
          Les présentes Conditions Générales de Vente sont soumises au droit français.
        </p>
        <p>
          En cas de différend, les parties s'efforceront de rechercher une solution
          amiable avant toute action judiciaire.
        </p>
      </Section>

      <Section title="XVI. Contact">
        <p>Pour toute question relative aux présentes Conditions Générales de Vente :</p>
        <div className="mt-2 space-y-1">
          <p className="font-semibold text-navy-deep">MP Advisory</p>
          <p>Madame Magalie Pichon</p>
          <p>
            Email :{" "}
            <a href="mailto:rh@mpadvisory.fr" className="text-purple-accent hover:underline">
              rh@mpadvisory.fr
            </a>
          </p>
          <p>
            Téléphone :{" "}
            <a href="tel:+33683175127" className="text-purple-accent hover:underline">
              06 83 17 51 27
            </a>
          </p>
          <p>
            Site :{" "}
            <a
              href="https://mpadvisory.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-accent hover:underline"
            >
              https://mpadvisory.fr
            </a>
          </p>
        </div>
      </Section>
    </div>
  );
}
